import React, { useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../config/supabase';

const BASE_FOLDERS = ['Inbox', 'Bookings', 'Sent', 'Drafts', 'Archive'];

function normalizeFolderName(value) {
 const name=String(value||'').trim();return ({inbox:'Inbox',sentitems:'Sent','sent items':'Sent'})[name.toLowerCase()]||name;
}
export function mailboxMessages(session,folder='Inbox',foldersBySession={},foldersByMessage={}) {
 if (folder === 'Archive' ? !session.archived_at : Boolean(session.archived_at)) return [];
 return (Array.isArray(session.raw_message_log)?session.raw_message_log:[]).filter((entry)=>{
  if(entry.deleted || entry.outside_folders || entry.policy_hidden)return false;
  if(folder==='Archive')return true;
  if(folder==='Bookings')return Boolean(session.account_id || session.capture_checklist);
  const actual=entry.provider_message_id?(foldersByMessage[entry.provider_message_id] || entry.sync_folder || entry.email_folder || 'Inbox')
   :(foldersBySession[session.id] || entry.email_folder || (session.archived_at?'Archive':'Inbox'));
  return normalizeFolderName(actual)===folder;
 });
}
export function buildMailboxEmailRows({sessions=[],mailboxId='',folder='Inbox',foldersBySession={},foldersByMessage={},filter='all',search='',contacts=[]}){
 const wanted=search.trim().toLowerCase();
 return sessions.filter((session)=>{
  if(session.channel!=='email'||(mailboxId&&session.email_provider_id!==mailboxId))return false;
  const messages=mailboxMessages(session,folder,foldersBySession,foldersByMessage),latest=messages[messages.length-1];
  if(!latest)return false;
  const contact=contacts.find((row)=>row.emails?.some((email)=>email.toLowerCase()===String(session.contact_email||'').toLowerCase()));
  if(filter==='customers'&&!contact)return false;
  if(filter==='unread'&&!messages.some((e)=>e.direction==='inbound'&&e.is_read!==true))return false;
  return !wanted || `${session.email_subject||''} ${session.contact_email||''} ${messages.map((e)=>e.body||'').join(' ')}`.toLowerCase().includes(wanted);
 });
}

function MailStatus({ mailbox }) {
    if (!mailbox) return null;
    const label = mailbox.status === 'connected' ? 'Connected' : mailbox.status === 'paused' ? 'Paused' : 'Needs attention';
    return <span className="rounded-full border border-[#03B7C9] px-2 py-0.5 text-[11px] text-[#03B7C9]">{label}</span>;
}

export default function EmailMailboxWorkspace({
    orgId,
    sessions = [],
    contacts = [],
    accounts = [],
    currentUserId,
    theme = 'light',
    onOpenConversation,
}) {
    const [mailboxes, setMailboxes] = useState([]);
    const [mailboxId, setMailboxId] = useState('');
    const [folder, setFolder] = useState('Inbox');
    const [filters, setFilters] = useState('all');
    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState('');
    const [foldersBySession, setFoldersBySession] = useState({});
    const [foldersByMessage,setFoldersByMessage]=useState({});
    const [moveTo, setMoveTo] = useState('');
    const [error, setError] = useState('');
    const dark = theme === 'dark';

    useEffect(() => {
        let active = true;
        async function loadMailboxData() {
            if (!orgId) return;
            const [mailboxResult, folderResult] = await Promise.all([
                supabaseClient.from('org_email_providers')
                    .select('id, from_email, from_name, provider_type, brand_entity_id, status, last_synced_at, access_mode, folder_sync')
                    .eq('org_id', orgId)
                    .eq('is_active', true)
                    .order('created_at', { ascending: false }),
                supabaseClient.from('org_email_conversation_folders')
                    .select('session_id, folder_name')
                    .eq('org_id', orgId),
            ]);
            if (!active) return;
            if (mailboxResult.error) setError(mailboxResult.error.message);
            const nextMailboxes = mailboxResult.data || [];
            setMailboxes(nextMailboxes);
            setMailboxId((current) => current || nextMailboxes[0]?.id || '');
            if (!folderResult.error) {
                setFoldersBySession(Object.fromEntries((folderResult.data || []).map((row) => [row.session_id, row.folder_name])));
            }
        }
        loadMailboxData().catch((loadError) => active && setError(loadError.message || 'Could not load email mailboxes.'));
        return () => { active = false; };
    }, [orgId]);

    const selectedMailbox = mailboxes.find((mailbox) => mailbox.id === mailboxId) || null;
    const folders = useMemo(() => [
        ...BASE_FOLDERS,
        ...((selectedMailbox?.folder_sync?.folders || []).filter((name) => !BASE_FOLDERS.includes(name))),
    ], [selectedMailbox]);
    const visibleSessions = useMemo(() => buildMailboxEmailRows({
        sessions,
        mailboxId,
        folder,
        foldersBySession, foldersByMessage,
        filter: filters,
        search,
        contacts,
    }), [contacts, filters, folder, foldersBySession, foldersByMessage, mailboxId, search, sessions]);
    const selectedSession = visibleSessions.find((session) => session.id === selectedId) || visibleSessions[0] || null;
    const inFolder=selectedSession?mailboxMessages(selectedSession,folder,foldersBySession,foldersByMessage):[];
    const selectedMessage=inFolder[inFolder.length-1]||{};
    const matchedContact = selectedSession && contacts.find((contact) =>
        contact.emails?.some((email) => email.toLowerCase() === String(selectedSession.contact_email || '').toLowerCase()));
    const linkedAccount = selectedSession ? accounts.find((account) => account.id === selectedSession.account_id) : null;

    async function moveMessage() {
        if (!selectedSession || !moveTo || !orgId) return;
        if (!selectedMessage.provider_message_id) { setError('Sync this mailbox before moving the message.'); return; }
        const result = await supabaseClient.functions.invoke('email-sync-action', { body: { sessionId: selectedSession.id, messageId: selectedMessage.provider_message_id, action: 'move', value: moveTo } });
        const saveError = result.error || (result.data?.error ? {message:result.data.error} : null);
        if (saveError) setError(saveError.message || 'Could not move the email.');
        else {
            setFoldersByMessage((current)=>({...current,[selectedMessage.provider_message_id]:moveTo}));
            setMoveTo('');
        }
    }

    const panel = dark ? 'bg-[#0F151F] border-[#232B38] text-gray-100' : 'bg-white border-gray-200 text-gray-900';
    const muted = dark ? 'text-gray-400' : 'text-gray-500';
    return (
        <div className="flex h-full min-h-[540px] min-w-0 gap-2" aria-label="Email workspace">
            <aside className={`w-56 shrink-0 rounded-lg border p-3 ${panel}`}>
                <label className={`mb-3 block text-xs font-semibold uppercase tracking-wide ${muted}`}>
                    Mailbox
                    <select aria-label="Email mailbox" value={mailboxId} onChange={(event) => { setMailboxId(event.target.value); setSelectedId(''); }} className="mt-1 w-full rounded border bg-transparent px-2 py-2 text-sm normal-case">
                        {mailboxes.map((mailbox) => <option key={mailbox.id} value={mailbox.id}>{mailbox.from_email}</option>)}
                    </select>
                </label>
                <h2 className={`mb-2 text-xs font-semibold uppercase tracking-wide ${muted}`}>Folders</h2>
                <nav className="space-y-1" aria-label="Email folders">
                    {folders.map((name) => {
                        const count = buildMailboxEmailRows({ sessions, mailboxId, folder: name, foldersBySession, foldersByMessage, contacts }).length;
                        return <button key={name} type="button" onClick={() => setFolder(name)} aria-current={folder === name ? 'page' : undefined} className={`flex w-full justify-between rounded px-2 py-2 text-left text-sm ${folder === name ? 'bg-[#03B7C9]/10 text-[#03B7C9]' : ''}`}><span>✉ {name}</span><span className={muted}>{count || ''}</span></button>;
                    })}
                </nav>
                {mailboxes.filter((mailbox) => mailbox.id !== mailboxId).length > 0 && (
                    <section className="mt-5 border-t border-[var(--surface-border)] pt-4" aria-label="Other mailboxes">
                        <h2 className={`mb-2 text-xs font-semibold uppercase tracking-wide ${muted}`}>Other mailboxes</h2>
                        <nav className="space-y-1" aria-label="Other mailboxes">
                            {mailboxes.filter((mailbox) => mailbox.id !== mailboxId).map((mailbox) => (
                                <button
                                    key={mailbox.id}
                                    type="button"
                                    onClick={() => { setMailboxId(mailbox.id); setFolder('Inbox'); setSelectedId(''); }}
                                    className="flex w-full justify-between rounded px-2 py-2 text-left text-xs hover:bg-[var(--surface-sunken)]"
                                >
                                    <span className="truncate">{mailbox.from_email}</span>
                                    <span className="ml-2 text-[var(--ink-muted)]">{mailbox.label || 'Mailbox'}</span>
                                </button>
                            ))}
                        </nav>
                    </section>
                )}
            </aside>
            <section className={`w-[340px] shrink-0 rounded-lg border ${panel}`} aria-label="Email message list">
                <div className="space-y-3 border-b border-inherit p-3">
                    <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} aria-label="Search email" placeholder="Search email…" className="w-full rounded border bg-transparent px-3 py-2 text-sm" />
                    <div className="flex gap-2" role="group" aria-label="Email filters">
                        {['all', 'unread', 'customers'].map((filter) => <button key={filter} type="button" aria-pressed={filters === filter} onClick={() => setFilters(filter)} className={`ia-hover-pulse rounded-full border px-2.5 py-1 text-xs ${filters === filter ? 'ia-selected-pill bg-transparent' : 'border-gray-400 text-gray-500'}`}>{filter === 'all' ? 'All' : filter === 'unread' ? 'Unread' : 'Customers'}</button>)}
                    </div>
                </div>
                <div className="h-[calc(100%-88px)] overflow-y-auto">
                    {error && <p role="alert" className="p-3 text-xs text-red-500">{error}</p>}
                    {visibleSessions.length === 0 ? <p className={`p-5 text-sm ${muted}`}>{mailboxes.length ? 'No email conversations in this folder.' : 'No email mailboxes are shared with you.'}</p> : visibleSessions.map((session) => {
                        const messages=mailboxMessages(session,folder,foldersBySession,foldersByMessage);
                        const message=messages[messages.length-1]||{};
                        const contact = contacts.find((item) => item.emails?.some((email) => email.toLowerCase() === String(session.contact_email || '').toLowerCase()));
                        return (
                            <button key={session.id} type="button" onClick={() => setSelectedId(session.id)} aria-current={selectedSession?.id === session.id ? 'true' : undefined} className={`w-full border-b border-inherit px-3 py-3 text-left ${selectedSession?.id === session.id ? 'bg-[#03B7C9]/10' : ''}`}>
                                <span className="flex items-start justify-between gap-2"><strong className="truncate text-sm">{message.from || session.contact_email || 'Unknown sender'}</strong><time className={`shrink-0 text-[11px] ${muted}`}>{message.at ? new Date(message.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''}</time></span>
                                <span className="mt-1 block truncate text-sm">{session.email_subject || '(no subject)'}</span>
                                <span className={`mt-1 block truncate text-xs ${muted}`}>{message.body || 'No message preview'}</span>
                                {contact && <span className="mt-2 inline-block rounded-full border border-[#03B7C9] px-2 py-0.5 text-[10px] text-[#03B7C9]">CRM: {contact.name}</span>}
                            </button>
                        );
                    })}
                </div>
            </section>
            <main className={`min-w-0 flex-1 rounded-lg border p-5 ${panel}`} aria-label="Email reading pane">
                {selectedSession ? (
                    <>
                        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-inherit pb-4">
                            <div><h2 className="text-lg font-semibold">{selectedSession.email_subject || '(no subject)'}</h2><p className={`mt-1 text-sm ${muted}`}>From {selectedMessage.from || selectedSession.contact_email || 'Unknown sender'} · {selectedMailbox?.from_email || ''}</p></div>
                            {linkedAccount && <span className="rounded-full border border-[#03B7C9] px-2 py-1 text-xs text-[#03B7C9]">Linked: {linkedAccount.account_name}</span>}
                        </div>
                        <div className="whitespace-pre-wrap py-5 text-sm">{selectedMessage.body || 'No message body.'}</div>
                        <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-inherit pt-4">
                            <button type="button" onClick={() => onOpenConversation?.(selectedSession.id)} className="rounded-full bg-[#03B7C9] px-4 py-2 text-sm font-semibold text-white">Reply</button>
                            <a href="/schedule" className="rounded-full border border-[#03B7C9] px-4 py-2 text-sm text-[#03B7C9]">Create booking</a>
                            <label className="ml-auto flex items-center gap-2 text-sm">Move selected email to folder
                                <select value={moveTo} onChange={(event) => setMoveTo(event.target.value)} className="rounded border bg-transparent px-2 py-2">
                                    <option value="">Choose folder</option>
                                    {folders.filter((name) => !['Bookings', 'Drafts'].includes(name)).map((name) => <option key={name} value={name}>{name}</option>)}
                                </select>
                            </label>
                            <button type="button" onClick={moveMessage} disabled={!moveTo} className="rounded-full border px-3 py-2 text-sm disabled:opacity-50">Move email</button>
                        </div>
                    </>
                ) : <p className={`pt-12 text-center text-sm ${muted}`}>Choose an email to read it.</p>}
            </main>
        </div>
    );
}
